/**
 * 本地存储工具类
 * 提供统一的本地存储接口，支持浏览器localStorage和uni-app存储
 */
class LocalStorageManager {
    constructor() {
        this.storage = this.getStorageEngine();
    }

    /**
     * 获取存储引擎
     * @returns {Object} 存储引擎对象
     */
    getStorageEngine() {
        // #ifdef H5
        if (typeof localStorage !== "undefined") {
            return {
                setItem: (key, value) => localStorage.setItem(key, value),
                getItem: (key) => localStorage.getItem(key),
                removeItem: (key) => localStorage.removeItem(key),
                clear: () => localStorage.clear(),
                key: (index) => localStorage.key(index)
            };
        }
        // #endif

        // #ifdef MP-WEIXIN || APP-PLUS
        if (typeof uni !== "undefined" && uni.setStorageSync) {
            return {
                setItem: (key, value) => uni.setStorageSync(key, value),
                getItem: (key) => uni.getStorageSync(key),
                removeItem: (key) => uni.removeStorageSync(key),
                clear: () => {
                    try {
                        uni.clearStorageSync();
                    }
                    catch (e) {
                        console.warn("Clear storage failed:", e);
                    }
                },
                key: (index) => {
                    try {
                        return uni.getStorageInfoSync().keys[index] || null;
                    }
                    catch (e) {
                        return null;
                    }
                }
            };
        }
        // #endif

        // 默认使用内存存储作为降级方案
        return {
            data: {},
            setItem: (key, value) => {
                this.data[key] = value;
            },
            getItem: (key) => {
                return this.data[key] || null;
            },
            removeItem: (key) => {
                delete this.data[key];
            },
            clear: () => {
                this.data = {};
            },
            key: (index) => {
                return Object.keys(this.data)[index] || null;
            }
        };
    }

    /**
     * 设置存储项
     * @param {string} key 存储键
     * @param {any} value 存储值
     */
    setItem(key, value) {
        try {
            const stringValue
                = typeof value === "string" ? value : JSON.stringify(value);
            this.storage.setItem(key, stringValue);
        }
        catch (error) {
            console.error("LocalStorage setItem error:", error);
        }
    }

    /**
     * 获取存储项
     * @param {string} key 存储键
     * @param {any} defaultValue 默认值
     * @returns {any} 存储值
     */
    getItem(key, defaultValue = null) {
        try {
            const value = this.storage.getItem(key);
            if (value === null || value === undefined) {
                return defaultValue;
            }

            // 尝试解析JSON
            try {
                return JSON.parse(value);
            }
            catch (e) {
                return value;
            }
        }
        catch (error) {
            console.error("LocalStorage getItem error:", error);
            return defaultValue;
        }
    }

    /**
     * 移除存储项
     * @param {string} key 存储键
     */
    removeItem(key) {
        try {
            this.storage.removeItem(key);
        }
        catch (error) {
            console.error("LocalStorage removeItem error:", error);
        }
    }

    /**
     * 清空所有存储
     */
    clear() {
        try {
            this.storage.clear();
        }
        catch (error) {
            console.error("LocalStorage clear error:", error);
        }
    }

    /**
     * 检查存储是否可用
     * @returns {boolean} 是否可用
     */
    isAvailable() {
        try {
            const testKey = "__storage_test__";
            this.storage.setItem(testKey, "test");
            this.storage.removeItem(testKey);
            return true;
        }
        catch (error) {
            return false;
        }
    }

    /**
     * 获取所有存储的键
     * @returns {Array} 键数组
     */
    getAllKeys() {
        try {
            const keys = [];
            for (let i = 0; i < 100; i++) {
                // 限制循环次数避免无限循环
                const key = this.storage.key(i);
                if (key) {
                    keys.push(key);
                }
                else {
                    break;
                }
            }
            return keys;
        }
        catch (error) {
            console.error("LocalStorage getAllKeys error:", error);
            return [];
        }
    }

    /**
     * 获取所有存储的数据
     * @returns {Object} 所有存储数据
     */
    getAll() {
        try {
            const result = {};
            const keys = this.getAllKeys();
            keys.forEach((key) => {
                result[key] = this.getItem(key);
            });
            return result;
        }
        catch (error) {
            console.error("LocalStorage getAll error:", error);
            return {};
        }
    }
}

// 创建全局实例
const localStorageManager = new LocalStorageManager();

// 导出常用的存储方法
export const setStorage = (key, value) =>
    localStorageManager.setItem(key, value);
export const getStorage = (key, defaultValue) =>
    localStorageManager.getItem(key, defaultValue);
export const removeStorage = (key) => localStorageManager.removeItem(key);
export const clearStorage = () => localStorageManager.clear();

// 导出实例，供复杂操作使用
export default localStorageManager;

// 定义常用的存储键常量
export const STORAGE_KEYS = {
    TOKEN: "auth_token",
    USER_UUID: "user_uuid",
    USER_NAME: "user_name",
    USER_TYPE: "user_type",
    LOGIN_TIME: "login_time"
};

// 便捷方法：保存用户登录信息
export const saveUserInfo = (userInfo) => {
    if (userInfo) {
        setStorage(STORAGE_KEYS.USER_UUID, userInfo.userUuid);
        setStorage(STORAGE_KEYS.USER_NAME, userInfo.userName);
        setStorage(STORAGE_KEYS.USER_TYPE, userInfo.userType);
        setStorage(STORAGE_KEYS.TOKEN, userInfo.userToken);
        setStorage(STORAGE_KEYS.LOGIN_TIME, new Date().toISOString());
    }
};

// 便捷方法：获取用户登录信息
export const getUserInfo = () => {
    return {
        userUuid: getStorage(STORAGE_KEYS.USER_UUID),
        userName: getStorage(STORAGE_KEYS.USER_NAME),
        userType: getStorage(STORAGE_KEYS.USER_TYPE),
        userToken: getStorage(STORAGE_KEYS.TOKEN),
        loginTime: getStorage(STORAGE_KEYS.LOGIN_TIME)
    };
};

// 便捷方法：清除用户登录信息
export const clearUserInfo = () => {
    removeStorage(STORAGE_KEYS.USER_UUID);
    removeStorage(STORAGE_KEYS.USER_NAME);
    removeStorage(STORAGE_KEYS.USER_TYPE);
    removeStorage(STORAGE_KEYS.TOKEN);
    removeStorage(STORAGE_KEYS.LOGIN_TIME);
};



// WEBPACK FOOTER //
// ./src/utils/localStorage.js